import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import type { QueryClient } from '@tanstack/react-query'

import { ApiError, ignoreAlreadyGone } from '@/lib/api'
import type { AssistantContextItem } from '@/lib/assistant-context.generated'
import { assistantEffects } from '@/lib/assistant-effects.generated'
import type { AssistantBrowserCommand } from '@/lib/assistant-browser-commands.generated'
import { useAssistantBrowserCommandDispatcher } from '@/lib/assistant-browser-commands'
import { getSessionEpoch } from '@/lib/auth'

import {
  capabilityErrorText,
  outcomeFromFailure,
  outcomeText,
  streamErrorText,
  type AssistantMessageRole,
  type ResolvedOutcome,
} from './assistant-copy'
import {
  approveProposal,
  deleteSession,
  getSession,
  rejectProposal,
  streamAssistant,
  type AssistantEvent,
  type AssistantImage,
  type AssistantProposalOutcome,
} from '../../api/assistant-api'
import type { AssistantTier } from '../../api/assistant-tier-api'
import { assistantTierKeys } from '../../api/query-keys'
import { useAssistantTierConfig } from '../../lib/assistant-tier-queries'
import {
  applyAssistantEffects,
  applySessionChange,
  executionResult,
  isSessionChange,
  reportingCreation,
} from './session-effects'

type PanelMessage = {
  role: AssistantMessageRole
  text: string
  proposalId?: string
  destination?: { label: string; path: string }
}
type PendingProposal = { id: string; details: unknown; status: AssistantProposalOutcome; turn: number }
type TranscriptEntry =
  | { kind: 'message'; message: PanelMessage; index: number }
  | { kind: 'proposal'; proposal: PendingProposal }

function orderTranscript(messages: PanelMessage[], proposals: PendingProposal[]): TranscriptEntry[] {
  const entries: TranscriptEntry[] = []
  const placed = new Set<PendingProposal>()
  const placeProposals = (turn?: number) => {
    for (const proposal of proposals) {
      if (placed.has(proposal) || (turn !== undefined && proposal.turn !== turn)) continue
      placed.add(proposal)
      entries.push({ kind: 'proposal', proposal })
      messages.forEach((message, index) => {
        if (message.proposalId === proposal.id) entries.push({ kind: 'message', message, index })
      })
    }
  }
  let turn = -1
  messages.forEach((message, index) => {
    if (message.proposalId && proposals.some((proposal) => proposal.id === message.proposalId)) return
    if (message.role === 'user') {
      placeProposals(turn)
      turn += 1
    }
    entries.push({ kind: 'message', message, index })
  })
  placeProposals()
  return entries
}

type UseAssistantSessionOptions = {
  open: boolean
  setOpen: (open: boolean) => void
  toggleRef: RefObject<HTMLButtonElement | null>
  boundIdentity: string
  queryClient: QueryClient
  navigate: (path: string) => Promise<boolean>
  announce: (message: string) => void
  download: (name: string, contentType: string, content: string) => void
  context: AssistantContextItem[]
  selectedEntityId: string | null
}

const streamBlockErrorCodes = new Set(['AI_STREAM_UNREADABLE', 'AI_UI_COMMAND_UNSUPPORTED'])

const interruptedErrorCodes = new Set([
  'AI_STREAM_INCOMPLETE',
  'AI_STREAM_ABANDONED',
  'AI_TURN_STOPPED',
  'AI_TURN_NOT_RECORDED',
  'AI_REQUEST_TIMEOUT',
  'AI_REQUEST_FAILED',
  'FORBIDDEN',
])

export function useAssistantSession({
  open,
  setOpen,
  toggleRef,
  boundIdentity,
  queryClient,
  navigate,
  announce,
  download,
  context,
  selectedEntityId,
}: UseAssistantSessionOptions) {
  const dispatchAssistantCommand = useAssistantBrowserCommandDispatcher()
  const [input, setInput] = useState('')
  const [image, setImage] = useState<AssistantImage | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<PanelMessage[]>([])
  const [pendingProposals, setPendingProposals] = useState<PendingProposal[]>([])
  const [busy, setBusy] = useState(false)
  const [resolving, setResolving] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [refreshTick, setRefreshTick] = useState(0)
  const [completionCount, setCompletionCount] = useState(0)
  const [pickedTier, setPickedTier] = useState<AssistantTier | null>(null)
  const { data: tierConfig } = useAssistantTierConfig(open ? selectedEntityId : null)
  const tier = tierConfig && pickedTier && tierConfig.enabledTiers.includes(pickedTier)
    ? pickedTier
    : tierConfig?.defaultTier
  const currentSessionId = useRef<string | null>(null)
  const hydrationVersion = useRef(0)
  const renderedOutput = useRef(false)
  const needsRefresh = useRef(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const streamController = useRef<AbortController | null>(null)
  const [stopping, setStopping] = useState(false)
  const stopTimeout = useRef<number | null>(null)
  const awaitingStoppedTurn = useRef(false)
  const releaseInput = () => {
    if (stopTimeout.current === null) return
    window.clearTimeout(stopTimeout.current)
    stopTimeout.current = null
    setStopping(false)
    setCompletionCount((current) => current + 1)
  }
  const endStopping = () => {
    awaitingStoppedTurn.current = false
    releaseInput()
  }
  const settleStoppedTurn = useEffectEvent(endStopping)
  const wasStopping = useRef(false)
  useEffect(() => {
    if (wasStopping.current && !stopping) announce('Response stopped.')
    wasStopping.current = stopping
  }, [announce, stopping])
  const updateSessionId = (value: string | null) => {
    currentSessionId.current = value
    setSessionId(value)
  }

  const close = () => {
    setOpen(false)
    toggleRef.current?.focus()
  }
  useEffect(() => {
    if (!open) streamController.current?.abort()
  }, [open])
  useEffect(() => () => {
    streamController.current?.abort()
    currentSessionId.current = null
    needsRefresh.current = false
  }, [])
  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      toggleRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, setOpen, toggleRef])

  const setOutcome = (id: string, status: AssistantProposalOutcome) =>
    setPendingProposals((current) =>
      current.map((item) => (item.id === id ? { ...item, status } : item)),
    )

  const knownIdentity = useRef(boundIdentity)

  useEffect(() => {
    if (!open || streamController.current || !needsRefresh.current || !sessionId) return
    const identity = knownIdentity.current
    const version = ++hydrationVersion.current
    void getSession(sessionId).then((session) => {
      if (
        knownIdentity.current !== identity
        || currentSessionId.current !== sessionId
        || hydrationVersion.current !== version
      ) return
      if (!session.activeTurn || !awaitingStoppedTurn.current) {
        const transcript: PanelMessage[] = []
        const cards: PendingProposal[] = []
        for (const [index, turn] of session.turns.entries()) {
          transcript.push({ role: 'user', text: turn.userText })
          if (turn.assistantText) transcript.push({ role: 'assistant', text: turn.assistantText })
          for (const failure of turn.capabilityErrors)
            transcript.push({ role: 'notice', text: capabilityErrorText(failure) })
          if (turn.errorCode)
            transcript.push({ role: 'notice', text: streamErrorText(turn.errorCode) })
          for (const proposal of turn.proposals)
            cards.push({ id: proposal.id, details: proposal.details, status: proposal.outcome, turn: index })
        }
        setMessages((current) => [...transcript, ...current.filter((message) =>
          message.proposalId && cards.some((card) => card.id === message.proposalId && card.status === 'executed'),
        )])
        setPendingProposals(cards)
        setError('')
      }
      if (!session.activeTurn) {
        needsRefresh.current = false
        settleStoppedTurn()
        return
      }
      needsRefresh.current = true
      window.setTimeout(() => {
        if (
          knownIdentity.current === identity
          && currentSessionId.current === sessionId
          && needsRefresh.current
          && !streamController.current
        ) setRefreshTick((current) => current + 1)
      }, 250)
    }).catch((failure: unknown) => {
      if (
        knownIdentity.current !== identity
        || currentSessionId.current !== sessionId
        || hydrationVersion.current !== version
      ) return
      settleStoppedTurn()
      if (failure instanceof ApiError && failure.status === 404) {
        needsRefresh.current = false
        currentSessionId.current = null
        setSessionId(null)
        setMessages([])
        setPendingProposals([])
      } else {
        setError('The assistant is unavailable. Try again.')
      }
    })
  }, [open, refreshTick, sessionId])

  useEffect(() => {
    if (knownIdentity.current === boundIdentity) return
    knownIdentity.current = boundIdentity
    streamController.current?.abort()
    settleStoppedTurn()
    needsRefresh.current = false
    currentSessionId.current = null
    setSessionId(null)
    setMessages([])
    setPendingProposals([])
    setPickedTier(null)
    setImage(null)
    setError('')
  }, [boundIdentity])

  const lastMessageLength = messages.at(-1)?.text.length
  const pendingProposalSignature = pendingProposals.map((proposal) => proposal.status).join(',')
  useLayoutEffect(() => {
    const container = scrollRef.current
    if (container) container.scrollTop = container.scrollHeight
  }, [open, messages.length, lastMessageLength, pendingProposalSignature, busy, error])
  useLayoutEffect(() => {
    const input = inputRef.current
    const activeElement = document.activeElement
    if (
      completionCount > 0
      && open
      && resolving == null
      && (
        activeElement === document.body
        || activeElement === input
        || activeElement?.closest('form') === input?.form
      )
    ) input?.focus()
  }, [completionCount, open, resolving])

  const onEvent = (event: AssistantEvent, commands: AssistantBrowserCommand[], turn: number) => {
    if (event.type === 'session') {
      updateSessionId(event.sessionId)
    } else if (event.type === 'message') {
      renderedOutput.current = true
      setMessages((current) => {
        const previous = current.at(-1)
        return previous?.role === 'assistant' && !previous.destination
          ? [...current.slice(0, -1), { role: 'assistant', text: previous.text + event.text }]
          : [...current, { role: 'assistant', text: event.text }]
      })
    } else if (event.type === 'pending-proposal') {
      renderedOutput.current = true
      setPendingProposals((current) => [
        ...current,
        { id: event.id, details: event.details, status: 'pending', turn },
      ])
    } else if (event.type === 'capability-error') {
      renderedOutput.current = true
      setMessages((current) => [...current, { role: 'notice', text: capabilityErrorText(event) }])
    } else if (event.type === 'ui-command') {
      commands.push(event.command)
    } else if (event.type === 'file') {
      try {
        download(event.name, event.contentType, event.content)
        renderedOutput.current = true
      } catch {
        announce('Part of the assistant response could not be used.')
      }
    } else if (event.type === 'error') {
      if (streamBlockErrorCodes.has(event.code)) {
        announce('Part of the assistant response could not be used.')
      } else {
        renderedOutput.current = true
        if (interruptedErrorCodes.has(event.code)) needsRefresh.current = true
        setError(streamErrorText(event.code))
      }
    }
  }

  const send = async () => {
    const message = input.trim() || (image ? 'Upload the attached logo.' : '')
    if (!message || busy || stopping) return
    awaitingStoppedTurn.current = false
    hydrationVersion.current += 1
    const clientTurnId = crypto.randomUUID()
    const sent: PanelMessage = { role: 'user', text: message }
    const turn = messages.filter((item) => item.role === 'user').length
    setMessages((current) => [...current, sent])
    setInput('')
    setImage(null)
    setError('')
    setBusy(true)
    renderedOutput.current = false
    const controller = new AbortController()
    const commands: AssistantBrowserCommand[] = []
    streamController.current = controller
    let streamSessionId = sessionId
    let completed = false
    let terminalFailure = false
    try {
      await streamAssistant(
        { message, context, sessionId, clientTurnId, entityId: selectedEntityId ?? undefined, tier, image: image ?? undefined },
        (event) => {
          if (controller.signal.aborted) return
          if (event.type === 'session') streamSessionId = event.sessionId
          if (event.type === 'done') completed = true
          if (event.type === 'error' && !streamBlockErrorCodes.has(event.code)) terminalFailure = true
          onEvent(event, commands, turn)
        },
        controller.signal,
      )
      if (completed && !terminalFailure) {
        for (const command of commands) {
          if (controller.signal.aborted) return
          const handled = await dispatchAssistantCommand(command, controller.signal)
          if (controller.signal.aborted) return
          if (handled) renderedOutput.current = true
          announce(handled ? 'Assistant command completed.' : 'Assistant command could not be completed.')
        }
      }
      if (!controller.signal.aborted && !renderedOutput.current) {
        setError('The assistant could not complete that request.')
      }
    } catch (failure) {
      if (controller.signal.aborted) return
      if (failure instanceof ApiError && failure.code === 'SESSION_NOT_FOUND') {
        updateSessionId(null)
        setMessages([])
        setPendingProposals([])
        setInput(message)
        setImage(image)
        setError('That conversation expired. Send again to start a new one.')
      } else if (failure instanceof ApiError && failure.code === 'ASSISTANT_TIER_NOT_ENABLED') {
        void queryClient.invalidateQueries({ queryKey: assistantTierKeys.root })
        setPickedTier(null)
        setMessages((current) => current.filter((item) => item !== sent))
        setInput(message)
        setImage(image)
        setError(streamErrorText(failure.code))
      } else if (failure instanceof ApiError && failure.code) {
        setError(streamErrorText(failure.code))
      } else {
        if (streamSessionId) needsRefresh.current = true
        setError('The assistant is unavailable. Try again.')
      }
    } finally {
      if (controller.signal.aborted && streamSessionId) needsRefresh.current = true
      if (streamController.current === controller) {
        streamController.current = null
        if (needsRefresh.current && streamSessionId) setRefreshTick((current) => current + 1)
      }
      if (!streamSessionId && awaitingStoppedTurn.current)
        setMessages((current) => [...current, { role: 'notice', text: streamErrorText('AI_TURN_STOPPED') }])
      if (!needsRefresh.current || !streamSessionId) endStopping()
      setBusy(false)
      setCompletionCount((current) => current + 1)
    }
  }

  const stop = () => {
    const controller = streamController.current
    if (!controller || stopTimeout.current !== null) return
    setStopping(true)
    awaitingStoppedTurn.current = true
    stopTimeout.current = window.setTimeout(releaseInput, 10_000)
    controller.abort()
  }

  const startNew = async () => {
    const previous = sessionId
    streamController.current?.abort()
    endStopping()
    needsRefresh.current = false
    updateSessionId(null)
    setMessages([])
    setPendingProposals([])
    setPickedTier(null)
    setImage(null)
    setError('')
    setInput('')
    inputRef.current?.focus()
    if (previous) {
      try {
        await ignoreAlreadyGone(() => deleteSession(previous))
      } catch {
        setError('The previous conversation could not be cleared on the server.')
        return
      }
    }
    announce('Started a new conversation.')
  }

  const resolve = async (id: string, approved: boolean) => {
    if (resolving) return
    const identity = knownIdentity.current
    const approvingSessionId = currentSessionId.current
    let sessionEpoch = getSessionEpoch()
    setResolving(id)
    setError('')
    try {
      let created: ReturnType<typeof reportingCreation> = null
      if (approved) {
        const response = await approveProposal(id)
        if (
          getSessionEpoch() !== sessionEpoch
          || knownIdentity.current !== identity
          || currentSessionId.current !== approvingSessionId
        ) return
        const execution = executionResult(response.result)
        if (execution.effects.includes(assistantEffects.reportingChanged)) created = reportingCreation(execution.value)
        if (isSessionChange(execution.value)) {
          streamController.current?.abort()
          updateSessionId(null)
          setMessages([])
          setPendingProposals([])
        }
        const approvalSessionEpoch = await applySessionChange(
          execution.value,
          queryClient,
          selectedEntityId ?? undefined,
        )
        sessionEpoch = approvalSessionEpoch ?? sessionEpoch
        if (getSessionEpoch() !== sessionEpoch) return
        await applyAssistantEffects(execution.effects, queryClient, sessionEpoch)
        if (getSessionEpoch() !== sessionEpoch) return
      } else {
        await rejectProposal(id)
        if (getSessionEpoch() !== sessionEpoch) return
      }
      if (knownIdentity.current === identity && currentSessionId.current === approvingSessionId)
        hydrationVersion.current += 1
      const outcome: ResolvedOutcome = approved ? 'executed' : 'rejected'
      setOutcome(id, outcome)
      if (created && knownIdentity.current === identity && currentSessionId.current === approvingSessionId) {
        setMessages((current) => [...current, { role: 'assistant', proposalId: id, ...created }])
        announce(created.text)
        const opened = await navigate(created.destination.path).catch(() => false)
        if (!opened && knownIdentity.current === identity && currentSessionId.current === approvingSessionId)
          setError('Created successfully, but the page could not be opened. Use the link in the chat.')
      } else announce(outcomeText[outcome])
      inputRef.current?.focus()
    } catch (failure) {
      if (getSessionEpoch() !== sessionEpoch) return
      if (
        failure instanceof ApiError
        && failure.code === 'PROPOSAL_UNAVAILABLE'
        && sessionId
      ) {
        needsRefresh.current = true
        setRefreshTick((current) => current + 1)
        inputRef.current?.focus()
        return
      }
      if (failure instanceof ApiError && failure.code === 'PROPOSAL_PERMISSION_CHANGED') {
        setError('Your permissions changed. Try again if access is restored.')
        announce('Your permissions changed. The proposal was not approved.')
        inputRef.current?.focus()
        return
      }
      const outcome = outcomeFromFailure(failure)
      if (outcome) {
        setOutcome(id, outcome)
        announce(outcomeText[outcome])
        inputRef.current?.focus()
      } else {
        setError('That proposal could not be completed. Try again.')
      }
    } finally {
      setResolving(null)
      if (needsRefresh.current && knownIdentity.current === identity && currentSessionId.current === approvingSessionId)
        setRefreshTick((current) => current + 1)
    }
  }

  return {
    input,
    setInput,
    image,
    setImage,
    messages,
    pendingProposals,
    transcript: orderTranscript(messages, pendingProposals),
    busy,
    stopping,
    resolving,
    error,
    inputRef,
    scrollRef,
    close,
    send,
    stop,
    resolve,
    startNew,
    tierOptions: tierConfig && tier ? { enabledTiers: tierConfig.enabledTiers, tier } : null,
    pickTier: setPickedTier,
  }
}
